// Supplier Onboarding Service
// Handles supplier registration/onboarding data submission
import axios from 'axios';
import { config } from '@/config';

// Same base as supplier auth – hamesha .../api/v1 (kabhi /api/v1/v1 na ho)
const toV1Base = (base) => {
  const u = String(base || '').trim().replace(/\/+$/, '');
  if (!u) return 'http://localhost:8000/api/v1';
  try {
    return `${new URL(u).origin}/api/v1`;
  } catch {
    const origin = u.replace(/\/(api(\/v1)*)?\/?$/i, '').replace(/\/+$/, '') || u;
    return `${origin}/api/v1`;
  }
};

const SUPPLIER_V1_BASE = toV1Base(config.SUPPLIER_API_BASE_URL || config.API_BASE_URL);
const API_BASE_URL = config.API_BASE_URL;

// Supplier onboarding + images: backend at /api/v1/supplier-onboarding, /api/v1/images
const supplierApi = axios.create({
  baseURL: SUPPLIER_V1_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

const customerApi = axios.create({
  baseURL: toV1Base(API_BASE_URL),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

class SupplierOnboardingService {
  /**
   * Upload a file to Cloudinary via supplier platform backend
   * @param {File} file - The file to upload
   * @param {string} folder - Cloudinary folder (e.g., 'supplier/onboarding')
   * @returns {Promise<string>} - Cloudinary URL
   */
  async uploadFile(file, folder = 'supplier/onboarding') {
    try {
      // Use supplier platform API for onboarding file uploads
      const formData = new FormData();
      formData.append('file', file);
      
      let uploadToken = null;
      try {
        uploadToken =
          sessionStorage.getItem('supplier_onboarding_upload_token') ||
          localStorage.getItem('supplier_onboarding_upload_token');
      } catch (_) {}

      const headers = {};
      // Logged-in suppliers: cookie session (withCredentials). Pre-login OTP: capability Bearer (TA-048).
      if (uploadToken) {
        headers['Authorization'] = `Bearer ${uploadToken}`;
      }

      const uploadApi = axios.create({
        baseURL: SUPPLIER_V1_BASE,
        timeout: 30000,
        withCredentials: true,
      });
      
      const response = await uploadApi.post(`/images/upload?folder=${encodeURIComponent(folder)}`, formData, {
        headers: headers
      });
      
      return { url: response.data.url, documentId: response.data.documentId };
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload file';
      throw new Error(errorMessage);
    }
  }

  /**
   * Upload multiple files to Cloudinary
   * @param {File[]} files - Array of files to upload
   * @param {string} folder - Cloudinary folder
   * @returns {Promise<string[]>} - Array of Cloudinary URLs
   */
  async uploadFiles(files, folder = 'supplier/onboarding') {
    try {
      const uploadPromises = files.map(file => this.uploadFile(file, folder));
      const urls = await Promise.all(uploadPromises);
      return urls;
    } catch (error) {
      console.error('Error uploading files:', error);
      throw new Error(`Failed to upload files: ${error.message}`);
    }
  }

  /**
   * Submit supplier onboarding data
   * @param {Object} onboardingData - Complete onboarding data
   * @returns {Promise<Object>} - Response from API
   */
  async submitOnboarding(onboardingData) {
    try {
      console.log('Submitting onboarding with payload:', { ...onboardingData, id_front_url: onboardingData.id_front_url ? '[URL]' : null, id_back_url: onboardingData.id_back_url ? '[URL]' : null });
      
      const response = await supplierApi.post('/supplier-onboarding', onboardingData, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting onboarding:', error?.response?.data || error);
      const errorMessage =
        error.response?.data?.detail ||
        (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg || e).join(', ') : null) ||
        (typeof error.response?.data === 'object' ? JSON.stringify(error.response.data) : null) ||
        error.message ||
        'Failed to submit onboarding data';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get onboarding data by email
   * @param {string} email - Email address
   * @returns {Promise<Object>} - Onboarding data
   */
  async getOnboardingByEmail(email) {
    try {
      const response = await supplierApi.get(`/supplier-onboarding/email/${encodeURIComponent(email)}`);
      
      // Also save to localStorage as backup
      if (response.data) {
        try {
          const progressKey = `supplier_onboarding_progress_${email.toLowerCase().trim()}`;
          localStorage.setItem(progressKey, JSON.stringify({
            email: email.toLowerCase().trim(),
            current_step: response.data.current_step,
            onboarding_data: response.data.onboarding_data || response.data.step_data,
            saved_at: new Date().toISOString()
          }));
        } catch (localError) {
          
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error getting onboarding from backend:', error);
      
      if (error.response?.status === 404) {
        return null; // No onboarding found
      }

      // Fallback to localStorage only when backend is unavailable (not for 404 deletes)
      try {
        const progressKey = `supplier_onboarding_progress_${email.toLowerCase().trim()}`;
        const savedProgress = localStorage.getItem(progressKey);
        if (savedProgress) {
          const parsed = JSON.parse(savedProgress);
          console.log('Loading progress from localStorage fallback');
          return {
            email: parsed.email,
            current_step: parsed.current_step,
            onboarding_data: parsed.onboarding_data,
            step_data: parsed.onboarding_data // For backward compatibility
          };
        }
      } catch (localError) {
        console.error('Failed to load progress from localStorage:', localError);
      }
      
      // Return null instead of throwing - let component handle it
      return null;
    }
  }

  /**
   * Save onboarding progress (step tracking)
   * @param {string} email - Email address
   * @param {string} step - Current step (email, otp, step2, step3, etc.)
   * @param {Object} stepData - Data for current step
   * @returns {Promise<Object>} - Response from API
   */
  async saveOnboardingProgress(email, step, stepData = {}) {
    try {
      // Use PUT endpoint that matches backend route
      // Backend route is registered at /api/v1/supplier-onboarding
      const payload = {
        email: email,
        current_step: step,
        onboarding_data: stepData
      };
      
      // Use PUT endpoint - no auth required for progress saving
      // Note: supplierApi baseURL is SUPPLIER_API_BASE_URL/v1, so we use /supplier-onboarding/progress
      const response = await supplierApi.put(`/supplier-onboarding/progress`, payload);
      
      // Also save to localStorage as backup
      try {
        const progressKey = `supplier_onboarding_progress_${email.toLowerCase().trim()}`;
        localStorage.setItem(progressKey, JSON.stringify({
          email: email.toLowerCase().trim(),
          current_step: step,
          onboarding_data: stepData,
          saved_at: new Date().toISOString()
        }));
      } catch (localError) {
        
      }
      
      return response.data;
    } catch (error) {
      console.error('Error saving onboarding progress to backend:', error);
      
      // Fallback to localStorage if backend is unavailable
      try {
        const progressKey = `supplier_onboarding_progress_${email.toLowerCase().trim()}`;
        localStorage.setItem(progressKey, JSON.stringify({
          email: email.toLowerCase().trim(),
          current_step: step,
          onboarding_data: stepData,
          saved_at: new Date().toISOString()
        }));
        console.log('Progress saved to localStorage as fallback');
      } catch (localError) {
        console.error('Failed to save progress to localStorage:', localError);
      }
      
      // Don't throw error - progress saving is not critical
      return null;
    }
  }

  /**
   * Get current user info from supplier platform (if logged in)
   * @returns {Promise<Object|null>} - User info or null
   */
  async getSupplierUserInfo() {
    try {
      // Phase 3: cookie session → GET /me (never decode JWTs)
      const response = await supplierApi.get('/supplier/auth/me', {
        withCredentials: true,
      });
      if (response.data) {
        return {
          name: response.data.full_name || response.data.business_name || response.data.name || '',
          email: response.data.email || '',
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting supplier user info:', error?.response?.data || error);
      return null;
    }
  }

  /**
   * Submit complete supplier registration with file uploads
   * @param {Object} formData - Form data from registration page
   * @param {Object} files - File objects (logoFile, insuranceFile, etc.)
   * @returns {Promise<Object>} - Response from API
   */
  async submitCompleteRegistration(formData, files) {
    try {
      const {
        selectedBusinessType,
        employeeCount,
        businessName,
        businessPhone,
        businessPhoneCode,
        businessEmail,
        businessWebsite,
        businessRegistration,
        country,
        city,
        zipCode,
        shortBio,
        socialLinks,
        personCountry,
        personCity,
        personAddress,
        personZipCode,
        personPhoneCode,
        personContact,
        idType,
        requiresBackImage,
        user_name,
        user_email
      } = formData;

      // Upload files to Cloudinary
      const uploadPromises = [];
      const fileKeys = [];

      // Business Identity files (only for non-solo)
      if (selectedBusinessType !== 'solo') {
        if (files.logoFile) {
          uploadPromises.push(this.uploadFile(files.logoFile, 'supplier/onboarding/logo'));
          fileKeys.push('logo');
        }
        if (files.insuranceFile) {
          uploadPromises.push(this.uploadFile(files.insuranceFile, 'supplier/onboarding/insurance'));
          fileKeys.push('insurance');
        }
        if (files.documentFile) {
          uploadPromises.push(this.uploadFile(files.documentFile, 'supplier/onboarding/documents'));
          fileKeys.push('document');
        }
      }

      // ID Verification files
      if (files.idFrontFile) {
        uploadPromises.push(this.uploadFile(files.idFrontFile, 'supplier/onboarding/id'));
        fileKeys.push('idFront');
      }
      if (files.idBackFile && requiresBackImage) {
        uploadPromises.push(this.uploadFile(files.idBackFile, 'supplier/onboarding/id'));
        fileKeys.push('idBack');
      }

      // Wait for all uploads to complete
      const uploadedUrls = await Promise.all(uploadPromises);

      // Map URLs to their respective fields
      let urlIndex = 0;
      const urlMap = {};
      fileKeys.forEach(key => {
        urlMap[key] = uploadedUrls[urlIndex++];
      });

      // Determine the email to use - prioritize user_email, then businessEmail, then fallback
      // Also try to get from token if not provided
      let primaryEmail = user_email || businessEmail || formData.email || '';
      
      // If still no email, try to get from supplier platform token
      if (!primaryEmail) {
        try {
          const supplierInfo = await this.getSupplierUserInfo();
          if (supplierInfo?.email) {
            primaryEmail = supplierInfo.email;
            console.log('Retrieved email from supplier platform:', primaryEmail);
          }
        } catch (err) {
          
        }
      }
      
      if (!primaryEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryEmail)) {
        console.error('Email validation failed. user_email:', user_email, 'businessEmail:', businessEmail, 'formData.email:', formData.email);
        throw new Error('Please provide a valid email for registration. If you signed in with Google, please ensure you are properly authenticated.');
      }
      
      // Prepare onboarding payload
      const onboardingPayload = {
        email: primaryEmail,
        user_name: user_name || null,
        user_email: user_email || null,
        business_type: selectedBusinessType,
        employee_count: employeeCount || 0,
      };

      // Business Identity (only for non-solo)
      if (selectedBusinessType !== 'solo') {
        onboardingPayload.business_name = businessName || '';
        onboardingPayload.business_phone = businessPhone || '';
        onboardingPayload.business_phone_code = businessPhoneCode || '';
        if (businessEmail) {
          onboardingPayload.business_email = businessEmail;
        }
        onboardingPayload.business_website = businessWebsite || '';
        onboardingPayload.business_registration = businessRegistration || '';
        onboardingPayload.business_country = country || '';
        onboardingPayload.business_city = city || '';
        onboardingPayload.business_zip_code = zipCode || '';
        onboardingPayload.short_bio = shortBio || '';
        onboardingPayload.social_links = socialLinks?.filter(link => link && link.trim()) || [];
        onboardingPayload.logo_url = urlMap.logo || null;
        onboardingPayload.insurance_document_url = urlMap.insurance || null;
        onboardingPayload.business_document_url = urlMap.document || null;
      }

      // ID Verification (always required)
      onboardingPayload.person_country = personCountry || '';
      onboardingPayload.person_city = personCity || '';
      onboardingPayload.person_address = personAddress || '';
      onboardingPayload.person_zip_code = personZipCode || '';
      onboardingPayload.person_phone_code = personPhoneCode || '';
      onboardingPayload.person_contact = personContact || '';
      onboardingPayload.id_type = idType || '';
      onboardingPayload.id_front_url = urlMap.idFront || null;
      onboardingPayload.id_back_url = urlMap.idBack || null;

      // Submit to API
      const response = await this.submitOnboarding(onboardingPayload);
      return response;
    } catch (error) {
      console.error('Error in complete registration:', error);
      throw error;
    }
  }
}

export default new SupplierOnboardingService();

